import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabState, useAutoRefresh } from '../hooks/useRealtimeState';
import API from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Video, User, Clock, Calendar, Search, Check, X, AlertCircle,
  Loader2, FileText, ChevronDown, ChevronUp, ExternalLink,
  Phone, ClipboardList, Pill, FlaskConical, Download, Ban, Copy
} from 'lucide-react';

// Force Jitsi meeting links to open in English, regardless of browser language
const joinMeetingUrl = (link) => {
  if (!link) return link;
  try {
    const u = new URL(link, window.location.origin);
    if (u.hostname.includes('jit.si')) u.searchParams.set('lang', 'en');
    return u.toString();
  } catch {
    return link;
  }
};

const copyMeetingLink = (link) => {
  navigator.clipboard.writeText(joinMeetingUrl(link));
  toast.success('Meeting link copied to clipboard');
};

export default function DoctorConsultationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [consultations, setConsultations] = useState([]);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useTabState('pending');
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog states
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showSoapDialog, setShowSoapDialog] = useState(false);
  const [showPrescriptionDialog, setShowPrescriptionDialog] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // SOAP notes state
  const [soapNotes, setSoapNotes] = useState({
    subjective: '', objective: '', assessment: '', plan: '',
    medicines: [], tests: [], advice: '', follow_up_date: null
  });
  const [soapMedSearch, setSoapMedSearch] = useState('');
  const [soapTestSearch, setSoapTestSearch] = useState('');
  const [catalogueMeds, setCatalogueMeds] = useState([]);
  const [catalogueTests, setCatalogueTests] = useState([]);
  const [searchingCatalogue, setSearchingCatalogue] = useState(false);

  // Prescription state
  const [prescription, setPrescription] = useState({
    diagnosis: '', medicines: [], tests: [], advice: '', follow_up_date: ''
  });
  const [newMedicine, setNewMedicine] = useState({ name: '', dosage: '', frequency: '', duration: '', instructions: '' });
  const [newTest, setNewTest] = useState({ name: '', instructions: '' });

  // Book for patient state
  const [showBookDialog, setShowBookDialog] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [bookForm, setBookForm] = useState({ reason: '', preferred_date: '', preferred_time: '', notes: '', is_instant: false });
  const [searchingPatients, setSearchingPatients] = useState(false);

  const searchPatients = async (q) => {
    if (!q || q.length < 2) { setPatientResults([]); return; }
    setSearchingPatients(true);
    try {
      const res = await API.get(`/patient-consultation/doctor/search-patients?search=${encodeURIComponent(q)}`);
      setPatientResults(res.data?.patients || []);
    } catch { setPatientResults([]); }
    setSearchingPatients(false);
  };

  const handleBookForPatient = async () => {
    if (!selectedPatient) { toast.error('Please select a patient'); return; }
    setSubmitting(true);
    try {
      const res = await API.post('/patient-consultation/doctor/book-for-patient', {
        patient_id: selectedPatient.id,
        reason: bookForm.reason,
        preferred_date: bookForm.preferred_date,
        preferred_time: bookForm.preferred_time,
        notes: bookForm.notes,
        is_instant: bookForm.is_instant,
      });
      toast.success(res.data?.message || 'Consultation booked!');
      setShowBookDialog(false);
      setSelectedPatient(null);
      setPatientSearch('');
      setBookForm({ reason: '', preferred_date: '', preferred_time: '', notes: '', is_instant: false });
      fetchData();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Booking failed');
    }
    setSubmitting(false);
  };

  const fetchData = useCallback(async () => {
    try {
      const statusMap = {
        pending: 'requested',
        upcoming: null, // will fetch approved + confirmed
        completed: 'completed',
        cancelled: null
      };

      const statusParam = statusMap[activeTab];
      let url = '/patient-consultation/doctor/consultations';
      if (statusParam) url += `?status=${statusParam}`;

      const requests = [API.get('/patient-consultation/doctor/stats')];

      if (activeTab === 'upcoming') {
        // Fetch both approved and confirmed
        requests.push(API.get('/patient-consultation/doctor/consultations?status=approved'));
        requests.push(API.get('/patient-consultation/doctor/consultations?status=confirmed'));
      } else if (activeTab === 'cancelled') {
        requests.push(API.get('/patient-consultation/doctor/consultations?status=cancelled'));
        requests.push(API.get('/patient-consultation/doctor/consultations?status=rejected'));
      } else {
        requests.push(API.get(url));
      }

      const results = await Promise.all(requests);
      const statsRes = results[0];

      let items = [];
      if (activeTab === 'upcoming' || activeTab === 'cancelled') {
        items = [...(results[1].data?.consultations || []), ...(results[2].data?.consultations || [])];
        items.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      } else {
        items = results[1].data?.consultations || [];
      }

      setConsultations(items);
      setStats(statsRes.data || {});
    } catch (err) {
      console.error('Fetch error:', err);
    }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, 15000);

  const handleApprove = async (consultation) => {
    setSubmitting(true);
    try {
      const res = await API.post(`/patient-consultation/doctor/approve/${consultation.id}`, {});
      if (res.data) {
        toast.success('Consultation approved! Meeting link generated.');
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
    setSubmitting(false);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Please provide a reason'); return; }
    setSubmitting(true);
    try {
      await API.post(`/patient-consultation/doctor/reject/${selectedConsultation.id}`, {
        reason: rejectReason
      });
      toast.success('Consultation rejected');
      setShowRejectDialog(false);
      setRejectReason('');
      setSelectedConsultation(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
    setSubmitting(false);
  };

  const handleDoctorCancel = async () => {
    if (!cancelReason.trim()) { toast.error('Please provide a reason'); return; }
    setSubmitting(true);
    try {
      await API.post(`/patient-consultation/doctor/cancel/${selectedConsultation.id}`, {
        reason: cancelReason
      });
      toast.success('Consultation cancelled');
      setShowCancelDialog(false);
      setCancelReason('');
      setSelectedConsultation(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
    setSubmitting(false);
  };

  const handleComplete = async (consultation) => {
    setSubmitting(true);
    try {
      await API.post(`/patient-consultation/doctor/complete/${consultation.id}`);
      toast.success('Consultation marked as completed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete');
    }
    setSubmitting(false);
  };

  const handleSaveSoap = async () => {
    setSubmitting(true);
    try {
      const res = await API.post(`/patient-consultation/doctor/soap-notes/${selectedConsultation.id}`, soapNotes);
      toast.success('SOAP notes saved & prescription generated');
      setShowSoapDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save notes');
    }
    setSubmitting(false);
  };

  // Catalogue search for medicines
  const searchCatalogueMeds = async (query) => {
    setSoapMedSearch(query);
    if (query.length < 2) { setCatalogueMeds([]); return; }
    setSearchingCatalogue(true);
    try {
      const res = await API.get(`/billing/marketplace-products?search=${encodeURIComponent(query)}&product_type=medicine`);
      setCatalogueMeds(res.data?.products || []);
    } catch { setCatalogueMeds([]); }
    setSearchingCatalogue(false);
  };

  // Catalogue search for tests
  const searchCatalogueTests = async (query) => {
    setSoapTestSearch(query);
    if (query.length < 2) { setCatalogueTests([]); return; }
    setSearchingCatalogue(true);
    try {
      const res = await API.get(`/billing/marketplace-products?search=${encodeURIComponent(query)}&product_type=lab_test`);
      setCatalogueTests(res.data?.products || []);
    } catch { setCatalogueTests([]); }
    setSearchingCatalogue(false);
  };

  const addSoapMedicine = (med) => {
    const entry = { name: med.name || med.generic_name, dosage: med.dosage || med.strength || '', frequency: '', duration: '', instructions: '' };
    setSoapNotes(s => ({ ...s, medicines: [...(s.medicines || []), entry] }));
    setSoapMedSearch('');
    setCatalogueMeds([]);
  };

  const addSoapMedicineManual = () => {
    if (!newMedicine.name) return;
    setSoapNotes(s => ({ ...s, medicines: [...(s.medicines || []), { ...newMedicine }] }));
    setNewMedicine({ name: '', dosage: '', frequency: '', duration: '', instructions: '' });
  };

  const removeSoapMedicine = (idx) => {
    setSoapNotes(s => ({ ...s, medicines: (s.medicines || []).filter((_, i) => i !== idx) }));
  };

  const addSoapTest = (test) => {
    const entry = { name: test.name, instructions: test.instructions || '' };
    setSoapNotes(s => ({ ...s, tests: [...(s.tests || []), entry] }));
    setSoapTestSearch('');
    setCatalogueTests([]);
  };

  const addSoapTestManual = () => {
    if (!newTest.name) return;
    setSoapNotes(s => ({ ...s, tests: [...(s.tests || []), { ...newTest }] }));
    setNewTest({ name: '', instructions: '' });
  };

  const removeSoapTest = (idx) => {
    setSoapNotes(s => ({ ...s, tests: (s.tests || []).filter((_, i) => i !== idx) }));
  };

  const addMedicine = () => {
    if (!newMedicine.name) return;
    setPrescription(p => ({ ...p, medicines: [...p.medicines, { ...newMedicine }] }));
    setNewMedicine({ name: '', dosage: '', frequency: '', duration: '', instructions: '' });
  };

  const removeMedicine = (idx) => {
    setPrescription(p => ({ ...p, medicines: p.medicines.filter((_, i) => i !== idx) }));
  };

  const addTest = () => {
    if (!newTest.name) return;
    setPrescription(p => ({ ...p, tests: [...p.tests, { ...newTest }] }));
    setNewTest({ name: '', instructions: '' });
  };

  const removeTest = (idx) => {
    setPrescription(p => ({ ...p, tests: p.tests.filter((_, i) => i !== idx) }));
  };

  const handleSavePrescription = async () => {
    if (!prescription.diagnosis) { toast.error('Please add diagnosis'); return; }
    setSubmitting(true);
    try {
      await API.post(`/patient-consultation/doctor/prescription/${selectedConsultation.id}`, prescription);
      toast.success('Prescription saved');
      setShowPrescriptionDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save prescription');
    }
    setSubmitting(false);
  };

  const openSoapDialog = (consultation) => {
    setSelectedConsultation(consultation);
    const existing = consultation.soap_notes || {};
    setSoapNotes({
      subjective: existing.subjective || '',
      objective: existing.objective || '',
      assessment: existing.assessment || '',
      plan: existing.plan || '',
      medicines: existing.medicines || consultation.prescription?.medicines || [],
      tests: existing.tests || consultation.prescription?.tests || [],
      advice: existing.advice || consultation.prescription?.advice || '',
      follow_up_date: existing.follow_up_date || consultation.prescription?.follow_up_date || null
    });
    setSoapMedSearch('');
    setSoapTestSearch('');
    setCatalogueMeds([]);
    setCatalogueTests([]);
    setShowSoapDialog(true);
  };

  const openPrescriptionDialog = (consultation) => {
    setSelectedConsultation(consultation);
    setPrescription(consultation.prescription || {
      diagnosis: '', medicines: [], tests: [], advice: '', follow_up_date: ''
    });
    setShowPrescriptionDialog(true);
  };

  const openDetailDialog = (consultation) => {
    setSelectedConsultation(consultation);
    setShowDetailDialog(true);
  };

  const getStatusBadge = (status, paymentStatus) => {
    const map = {
      requested: <Badge variant="outline" className="border-amber-400 text-amber-600">Pending</Badge>,
      approved: paymentStatus === 'paid'
        ? <Badge className="bg-green-100 text-green-700 border-green-300">Paid</Badge>
        : <Badge className="bg-orange-100 text-orange-700 border-orange-300">Unpaid</Badge>,
      confirmed: <Badge className="bg-green-100 text-green-700 border-green-300">Confirmed & Paid</Badge>,
      completed: <Badge className="bg-teal-100 text-teal-700 border-teal-300">Completed</Badge>,
      cancelled: <Badge variant="destructive">Cancelled</Badge>,
      rejected: <Badge className="bg-red-100 text-red-700 border-red-300">Rejected</Badge>
    };
    return map[status] || <Badge variant="outline">{status}</Badge>;
  };

  const filteredConsultations = consultations.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return c.patient_name?.toLowerCase().includes(q) || c.reason?.toLowerCase().includes(q);
  });

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div data-testid="doctor-consultation-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Online Consultations
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage your online consultation requests</p>
        </div>
        <Button onClick={() => setShowBookDialog(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="book-for-patient-btn">
          <Video className="w-4 h-4 mr-2" /> Book for Patient
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="consultation-stats">
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <p className="text-sm text-amber-600 font-medium">Pending</p>
            <p className="text-2xl font-bold text-amber-700">{stats.pending || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <p className="text-sm text-green-600 font-medium">Confirmed</p>
            <p className="text-2xl font-bold text-green-700">{stats.confirmed || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-teal-200 bg-teal-50/50">
          <CardContent className="p-4">
            <p className="text-sm text-teal-600 font-medium">Completed</p>
            <p className="text-2xl font-bold text-teal-700">{stats.completed || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600 font-medium">Earnings</p>
            <p className="text-2xl font-bold text-blue-700">₹{stats.total_earnings || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="w-max min-w-full sm:w-auto">
              <TabsTrigger value="pending" data-testid="tab-pending" className="whitespace-nowrap">
                Pending {stats.pending > 0 && <span className="ml-1 bg-amber-500 text-white text-xs px-1.5 rounded-full">{stats.pending}</span>}
              </TabsTrigger>
              <TabsTrigger value="upcoming" data-testid="tab-upcoming" className="whitespace-nowrap">
                Upcoming {(stats.approved + stats.confirmed) > 0 && <span className="ml-1 bg-blue-500 text-white text-xs px-1.5 rounded-full">{(stats.approved || 0) + (stats.confirmed || 0)}</span>}
              </TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed" className="whitespace-nowrap">Completed</TabsTrigger>
              <TabsTrigger value="cancelled" data-testid="tab-cancelled" className="whitespace-nowrap">Cancelled</TabsTrigger>
            </TabsList>
          </div>
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by patient name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="consultation-search"
            />
          </div>
        </div>

        {/* Consultation List */}
        {filteredConsultations.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Video className="h-12 w-12 mx-auto mb-4 opacity-40" />
            <p>No consultations in this category</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredConsultations.map(c => (
              <Card key={c.id} className="hover:shadow-md transition-shadow" data-testid={`consultation-card-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    {/* Patient Info */}
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-2 bg-teal-100 rounded-full shrink-0">
                        <User className="h-5 w-5 text-teal-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-slate-900">{c.patient_name}</p>
                          {getStatusBadge(c.status, c.payment_status)}
                        </div>
                        {c.patient_phone && (
                          <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {c.patient_phone}
                          </p>
                        )}
                        <p className="text-sm text-slate-600 mt-1">{c.reason}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {c.scheduled_date || c.preferred_date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {c.scheduled_time || c.preferred_time || 'TBD'}
                          </span>
                          <span className="font-medium text-slate-600">₹{c.fee}</span>
                          {c.coupon_code && (
                            <Badge className="bg-violet-100 text-violet-700 text-[10px]">
                              Coupon: {c.coupon_code} (-₹{c.coupon_discount})
                            </Badge>
                          )}
                        </div>
                        {c.coupon_code && (
                          <p className="text-xs text-violet-600 mt-0.5">
                            Patient pays: ₹{c.final_fee ?? (c.fee - (c.coupon_discount || 0))} (Original: ₹{c.fee})
                          </p>
                        )}
                        {c.notes && <p className="text-xs text-slate-400 mt-1 italic">Note: {c.notes}</p>}

                        {/* Documents indicator */}
                        {c.documents && c.documents.length > 0 && (
                          <p className="text-xs text-blue-500 mt-1 flex items-center gap-1 cursor-pointer" onClick={() => openDetailDialog(c)}>
                            <FileText className="h-3 w-3" /> {c.documents.length} document(s) attached
                          </p>
                        )}

                        {/* Cancellation/Rejection info */}
                        {c.status === 'rejected' && c.rejection_reason && (
                          <p className="text-xs text-red-500 mt-1">Rejection reason: {c.rejection_reason}</p>
                        )}
                        {c.status === 'cancelled' && c.cancellation_reason && (
                          <p className="text-xs text-red-500 mt-1">
                            Cancelled by {c.cancelled_by}: {c.cancellation_reason}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {c.status === 'requested' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(c)}
                            disabled={submitting}
                            data-testid={`approve-btn-${c.id}`}
                          >
                            <Check className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => { setSelectedConsultation(c); setShowRejectDialog(true); }}
                            disabled={submitting}
                            data-testid={`reject-btn-${c.id}`}
                          >
                            <X className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </>
                      )}

                      {c.status === 'approved' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedConsultation(c); setShowCancelDialog(true); }}
                          disabled={submitting}
                        >
                          <Ban className="h-4 w-4 mr-1" /> Cancel
                        </Button>
                      )}

                      {c.status === 'confirmed' && (
                        <>
                          {c.meeting_link && (
                            <>
                              <Button size="sm" onClick={() => window.open(joinMeetingUrl(c.meeting_link), '_blank')} data-testid={`doctor-join-call-${c.id}`}>
                                <Video className="h-4 w-4 mr-1" /> Join Call
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => copyMeetingLink(c.meeting_link)}
                                data-testid={`doctor-copy-link-${c.id}`} title="Copy meeting link">
                                <Copy className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-teal-600"
                            onClick={() => openSoapDialog(c)}
                          >
                            <ClipboardList className="h-4 w-4 mr-1" /> SOAP Notes
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600"
                            onClick={() => openPrescriptionDialog(c)}
                          >
                            <FileText className="h-4 w-4 mr-1" /> Prescription
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleComplete(c)}
                            disabled={submitting}
                          >
                            <Check className="h-4 w-4 mr-1" /> Complete
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-500"
                            onClick={() => { setSelectedConsultation(c); setShowCancelDialog(true); }}
                            disabled={submitting}
                          >
                            <Ban className="h-4 w-4 mr-1" /> Cancel
                          </Button>
                        </>
                      )}

                      {c.status === 'completed' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-teal-600"
                            onClick={() => openSoapDialog(c)}
                          >
                            <ClipboardList className="h-4 w-4 mr-1" /> {c.soap_notes ? 'View Notes' : 'Add Notes'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600"
                            onClick={() => openPrescriptionDialog(c)}
                          >
                            <FileText className="h-4 w-4 mr-1" /> {c.prescription ? 'View Rx' : 'Add Rx'}
                          </Button>
                        </>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openDetailDialog(c)}
                      >
                        Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Consultation Request</DialogTitle>
            <DialogDescription>
              The patient will see this reason. Please be professional.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Patient</Label>
              <p className="text-sm font-medium">{selectedConsultation?.patient_name}</p>
            </div>
            <div>
              <Label>Reason for Rejection *</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., Not available on this date, please try another slot..."
                className="mt-1"
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectDialog(false); setRejectReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={submitting || !rejectReason.trim()} data-testid="confirm-reject-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <X className="h-4 w-4 mr-1" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Consultation</DialogTitle>
            <DialogDescription>
              {selectedConsultation?.payment_status === 'paid'
                ? 'The patient will receive a full refund.'
                : 'The patient will be notified of the cancellation.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reason for Cancellation *</Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Reason for cancellation..."
                className="mt-1"
                data-testid="cancel-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCancelDialog(false); setCancelReason(''); }}>Back</Button>
            <Button variant="destructive" onClick={handleDoctorCancel} disabled={submitting || !cancelReason.trim()} data-testid="confirm-cancel-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Cancel Consultation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Consultation Details</DialogTitle>
          </DialogHeader>
          {selectedConsultation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-400">Patient:</span> <span className="font-medium">{selectedConsultation.patient_name}</span></div>
                <div><span className="text-slate-400">Phone:</span> <span>{selectedConsultation.patient_phone}</span></div>
                <div><span className="text-slate-400">Date:</span> <span>{selectedConsultation.scheduled_date || selectedConsultation.preferred_date}</span></div>
                <div><span className="text-slate-400">Time:</span> <span>{selectedConsultation.scheduled_time || selectedConsultation.preferred_time}</span></div>
                <div><span className="text-slate-400">Fee:</span> <span className="font-medium">₹{selectedConsultation.fee}</span></div>
                <div><span className="text-slate-400">Status:</span> {getStatusBadge(selectedConsultation.status, selectedConsultation.payment_status)}</div>
                <div><span className="text-slate-400">Payment:</span> <span>{selectedConsultation.payment_status}</span></div>
              </div>

              <div>
                <p className="text-sm text-slate-400">Reason</p>
                <p className="text-sm">{selectedConsultation.reason}</p>
              </div>

              {selectedConsultation.notes && (
                <div>
                  <p className="text-sm text-slate-400">Patient Notes</p>
                  <p className="text-sm">{selectedConsultation.notes}</p>
                </div>
              )}

              {selectedConsultation.meeting_link && (
                <div>
                  <p className="text-sm text-slate-400">Meeting Link</p>
                  <div className="flex items-center gap-2">
                    <a href={joinMeetingUrl(selectedConsultation.meeting_link)} target="_blank" rel="noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1 truncate">
                      {selectedConsultation.meeting_link} <ExternalLink className="h-3 w-3" />
                    </a>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyMeetingLink(selectedConsultation.meeting_link)}
                      data-testid="doctor-detail-copy-link" title="Copy meeting link">
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Documents */}
              {selectedConsultation.documents && selectedConsultation.documents.length > 0 && (
                <div>
                  <p className="text-sm text-slate-400 mb-2">Uploaded Documents</p>
                  <div className="space-y-2">
                    {selectedConsultation.documents.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-400" />
                          <span className="text-sm">{doc.original_name}</span>
                        </div>
                        <Button size="sm" variant="ghost"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/patient-consultation/document/${doc.file_name}`, '_blank')}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SOAP Notes Preview */}
              {selectedConsultation.soap_notes && (
                <div>
                  <p className="text-sm text-slate-400 mb-1">SOAP Notes</p>
                  <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                    {selectedConsultation.soap_notes.subjective && <p><strong>S:</strong> {selectedConsultation.soap_notes.subjective}</p>}
                    {selectedConsultation.soap_notes.objective && <p><strong>O:</strong> {selectedConsultation.soap_notes.objective}</p>}
                    {selectedConsultation.soap_notes.assessment && <p><strong>A:</strong> {selectedConsultation.soap_notes.assessment}</p>}
                    {selectedConsultation.soap_notes.plan && <p><strong>P:</strong> {selectedConsultation.soap_notes.plan}</p>}
                  </div>
                </div>
              )}

              {/* Prescription Preview */}
              {selectedConsultation.prescription && (
                <div>
                  <p className="text-sm text-slate-400 mb-1">Prescription</p>
                  <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-2">
                    <p><strong>Diagnosis:</strong> {selectedConsultation.prescription.diagnosis}</p>
                    {selectedConsultation.prescription.medicines?.length > 0 && (
                      <div>
                        <strong>Medicines:</strong>
                        <ul className="ml-4 list-disc">
                          {selectedConsultation.prescription.medicines.map((m, i) => (
                            <li key={`li-${i}`}>{m.name} - {m.dosage}, {m.frequency}, {m.duration}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedConsultation.prescription.tests?.length > 0 && (
                      <div>
                        <strong>Tests:</strong>
                        <ul className="ml-4 list-disc">
                          {selectedConsultation.prescription.tests.map((t, i) => (
                            <li key={`li-${i}`}>{t.name}{t.instructions ? ` - ${t.instructions}` : ''}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedConsultation.prescription.advice && <p><strong>Advice:</strong> {selectedConsultation.prescription.advice}</p>}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SOAP Notes Dialog - with medicines/tests from catalogue */}
      <Dialog open={showSoapDialog} onOpenChange={setShowSoapDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>SOAP Notes & Prescription - {selectedConsultation?.patient_name}</DialogTitle>
            <DialogDescription>Notes and prescription will be generated together. Patient can download the prescription.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* SOAP Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Subjective</Label>
                <Textarea value={soapNotes.subjective} onChange={e => setSoapNotes(s => ({ ...s, subjective: e.target.value }))}
                  placeholder="Chief complaint..." className="mt-1 h-20" data-testid="soap-subjective" />
              </div>
              <div>
                <Label>Objective</Label>
                <Textarea value={soapNotes.objective} onChange={e => setSoapNotes(s => ({ ...s, objective: e.target.value }))}
                  placeholder="Vitals, exam findings..." className="mt-1 h-20" data-testid="soap-objective" />
              </div>
              <div>
                <Label>Assessment (Diagnosis)</Label>
                <Textarea value={soapNotes.assessment} onChange={e => setSoapNotes(s => ({ ...s, assessment: e.target.value }))}
                  placeholder="Diagnosis..." className="mt-1 h-20" data-testid="soap-assessment" />
              </div>
              <div>
                <Label>Plan</Label>
                <Textarea value={soapNotes.plan} onChange={e => setSoapNotes(s => ({ ...s, plan: e.target.value }))}
                  placeholder="Treatment plan..." className="mt-1 h-20" data-testid="soap-plan" />
              </div>
            </div>

            {/* Medicines from Catalogue */}
            <div>
              <Label className="flex items-center gap-1 mb-1"><Pill className="h-4 w-4" /> Medicines</Label>
              {(soapNotes.medicines || []).length > 0 && (
                <div className="space-y-1 mb-2">
                  {soapNotes.medicines.map((m, i) => (
                    <div key={`el-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                      <div className="flex-1">
                        <span className="font-medium">{m.name}</span>
                        {m.dosage && <span className="text-slate-400 ml-1">- {m.dosage}</span>}
                        <div className="flex gap-2 mt-1">
                          <Input className="h-7 text-xs w-24" placeholder="Frequency" value={m.frequency || ''}
                            onChange={e => { const meds = [...(soapNotes.medicines || [])]; meds[i] = { ...meds[i], frequency: e.target.value }; setSoapNotes(s => ({ ...s, medicines: meds })); }} />
                          <Input className="h-7 text-xs w-24" placeholder="Duration" value={m.duration || ''}
                            onChange={e => { const meds = [...(soapNotes.medicines || [])]; meds[i] = { ...meds[i], duration: e.target.value }; setSoapNotes(s => ({ ...s, medicines: meds })); }} />
                          <Input className="h-7 text-xs flex-1" placeholder="Instructions" value={m.instructions || ''}
                            onChange={e => { const meds = [...(soapNotes.medicines || [])]; meds[i] = { ...meds[i], instructions: e.target.value }; setSoapNotes(s => ({ ...s, medicines: meds })); }} />
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="ml-2 shrink-0" onClick={() => removeSoapMedicine(i)}><X className="h-3 w-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="relative">
                <Input placeholder="Search medicines from catalogue..." value={soapMedSearch}
                  onChange={e => searchCatalogueMeds(e.target.value)} className="h-9" data-testid="soap-med-search" />
                {catalogueMeds.length > 0 && (
                  <div className="absolute z-20 w-full bg-white border rounded-md shadow-lg mt-1 max-h-40 overflow-y-auto">
                    {catalogueMeds.map(m => (
                      <button key={m.id} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b last:border-0"
                        onClick={() => addSoapMedicine(m)}>
                        <span className="font-medium">{m.name}</span>
                        {m.generic_name && <span className="text-slate-400 ml-1">({m.generic_name})</span>}
                        {m.dosage && <span className="text-slate-400 ml-1">- {m.dosage}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-1">
                <Input className="h-8 text-xs flex-1" placeholder="Or add manually: medicine name" value={newMedicine.name}
                  onChange={e => setNewMedicine(m => ({ ...m, name: e.target.value }))} />
                <Input className="h-8 text-xs w-20" placeholder="Dosage" value={newMedicine.dosage}
                  onChange={e => setNewMedicine(m => ({ ...m, dosage: e.target.value }))} />
                <Button size="sm" variant="outline" className="h-8" onClick={addSoapMedicineManual} disabled={!newMedicine.name}>Add</Button>
              </div>
            </div>

            {/* Tests from Catalogue */}
            <div>
              <Label className="flex items-center gap-1 mb-1"><FlaskConical className="h-4 w-4" /> Lab Tests</Label>
              {(soapNotes.tests || []).length > 0 && (
                <div className="space-y-1 mb-2">
                  {soapNotes.tests.map((t, i) => (
                    <div key={`el-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                      <span>{t.name}{t.instructions ? ` - ${t.instructions}` : ''}</span>
                      <Button size="sm" variant="ghost" onClick={() => removeSoapTest(i)}><X className="h-3 w-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="relative">
                <Input placeholder="Search lab tests from catalogue..." value={soapTestSearch}
                  onChange={e => searchCatalogueTests(e.target.value)} className="h-9" data-testid="soap-test-search" />
                {catalogueTests.length > 0 && (
                  <div className="absolute z-20 w-full bg-white border rounded-md shadow-lg mt-1 max-h-40 overflow-y-auto">
                    {catalogueTests.map(t => (
                      <button key={t.id} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b last:border-0"
                        onClick={() => addSoapTest(t)}>
                        <span className="font-medium">{t.name}</span>
                        {t.sample_type && <span className="text-slate-400 ml-1">({t.sample_type})</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-1">
                <Input className="h-8 text-xs flex-1" placeholder="Or add manually: test name" value={newTest.name}
                  onChange={e => setNewTest(t => ({ ...t, name: e.target.value }))} />
                <Button size="sm" variant="outline" className="h-8" onClick={addSoapTestManual} disabled={!newTest.name}>Add</Button>
              </div>
            </div>

            {/* Advice & Follow-up */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Advice</Label>
                <Textarea value={soapNotes.advice || ''} onChange={e => setSoapNotes(s => ({ ...s, advice: e.target.value }))}
                  placeholder="General advice..." className="mt-1 h-16" />
              </div>
              <div>
                <Label>Follow-up Date</Label>
                <Input type="date" value={soapNotes.follow_up_date || ''}
                  onChange={e => setSoapNotes(s => ({ ...s, follow_up_date: e.target.value }))}
                  className="mt-1" min={new Date().toISOString().split('T')[0]} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSoapDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveSoap} disabled={submitting} data-testid="save-soap-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Notes & Generate Prescription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prescription Dialog */}
      <Dialog open={showPrescriptionDialog} onOpenChange={setShowPrescriptionDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Prescription - {selectedConsultation?.patient_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Diagnosis */}
            <div>
              <Label>Diagnosis *</Label>
              <Input value={prescription.diagnosis} onChange={e => setPrescription(p => ({ ...p, diagnosis: e.target.value }))}
                placeholder="Enter diagnosis..." className="mt-1" data-testid="rx-diagnosis" />
            </div>

            {/* Medicines */}
            <div>
              <Label className="flex items-center gap-1"><Pill className="h-4 w-4" /> Medicines</Label>
              {prescription.medicines?.length > 0 && (
                <div className="mt-2 space-y-2">
                  {prescription.medicines.map((m, i) => (
                    <div key={`el-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <div>
                        <span className="font-medium">{m.name}</span> - {m.dosage}, {m.frequency}, {m.duration}
                        {m.instructions && <span className="text-slate-400"> ({m.instructions})</span>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeMedicine(i)}><X className="h-3 w-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
                <Input placeholder="Medicine name" value={newMedicine.name}
                  onChange={e => setNewMedicine(m => ({ ...m, name: e.target.value }))} data-testid="med-name" />
                <Input placeholder="Dosage" value={newMedicine.dosage}
                  onChange={e => setNewMedicine(m => ({ ...m, dosage: e.target.value }))} />
                <Input placeholder="Frequency" value={newMedicine.frequency}
                  onChange={e => setNewMedicine(m => ({ ...m, frequency: e.target.value }))} />
                <Input placeholder="Duration" value={newMedicine.duration}
                  onChange={e => setNewMedicine(m => ({ ...m, duration: e.target.value }))} />
                <Button size="sm" onClick={addMedicine} disabled={!newMedicine.name} data-testid="add-med-btn">Add</Button>
              </div>
            </div>

            {/* Tests */}
            <div>
              <Label className="flex items-center gap-1"><FlaskConical className="h-4 w-4" /> Tests</Label>
              {prescription.tests?.length > 0 && (
                <div className="mt-2 space-y-2">
                  {prescription.tests.map((t, i) => (
                    <div key={`el-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <span>{t.name}{t.instructions ? ` - ${t.instructions}` : ''}</span>
                      <Button size="sm" variant="ghost" onClick={() => removeTest(i)}><X className="h-3 w-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input placeholder="Test name" value={newTest.name} className="col-span-2"
                  onChange={e => setNewTest(t => ({ ...t, name: e.target.value }))} data-testid="test-name" />
                <Button size="sm" onClick={addTest} disabled={!newTest.name} data-testid="add-test-btn">Add</Button>
              </div>
            </div>

            {/* Advice */}
            <div>
              <Label>Advice</Label>
              <Textarea value={prescription.advice} onChange={e => setPrescription(p => ({ ...p, advice: e.target.value }))}
                placeholder="General advice for the patient..." className="mt-1" />
            </div>

            {/* Follow-up */}
            <div>
              <Label>Follow-up Date</Label>
              <Input type="date" value={prescription.follow_up_date}
                onChange={e => setPrescription(p => ({ ...p, follow_up_date: e.target.value }))}
                className="mt-1" min={new Date().toISOString().split('T')[0]} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrescriptionDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePrescription} disabled={submitting || !prescription.diagnosis} data-testid="save-rx-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Prescription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Book for Patient Dialog */}
      <Dialog open={showBookDialog} onOpenChange={setShowBookDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Book Consultation for Patient</DialogTitle>
            <DialogDescription>Search your patients and book an online consultation on their behalf</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!selectedPatient ? (
              <div>
                <Label>Search Patient (Name, Phone, Email, or ID)</Label>
                <div className="relative">
                  <Input
                    placeholder="Type to search..."
                    value={patientSearch}
                    onChange={(e) => { setPatientSearch(e.target.value); searchPatients(e.target.value); }}
                    data-testid="patient-search-input"
                  />
                  {searchingPatients && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-slate-400" />}
                </div>
                {patientResults.length > 0 && (
                  <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                    {patientResults.map((p) => (
                      <button
                        key={p.id}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 flex items-center justify-between"
                        onClick={() => { setSelectedPatient(p); setPatientResults([]); }}
                        data-testid={`patient-result-${p.id}`}
                      >
                        <div>
                          <p className="font-medium text-sm">{p.name}</p>
                          <p className="text-xs text-slate-500">{p.phone} {p.email && `| ${p.email}`}</p>
                        </div>
                        {p.has_portal_account ? (
                          <Badge className="bg-green-100 text-green-700 text-[10px]">Portal Active</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 text-[10px]">No Portal</Badge>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {patientSearch.length >= 2 && patientResults.length === 0 && !searchingPatients && (
                  <p className="text-xs text-slate-500 mt-2">No patients found</p>
                )}
              </div>
            ) : (
              <div className="bg-teal-50 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{selectedPatient.name}</p>
                  <p className="text-xs text-slate-500">{selectedPatient.phone} {selectedPatient.email && `| ${selectedPatient.email}`}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedPatient(null); setPatientSearch(''); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_instant"
                checked={bookForm.is_instant}
                onChange={(e) => setBookForm({...bookForm, is_instant: e.target.checked})}
              />
              <Label htmlFor="is_instant" className="text-sm cursor-pointer">Instant consultation (generate meeting link now)</Label>
            </div>

            {!bookForm.is_instant && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={bookForm.preferred_date}
                    onChange={(e) => setBookForm({...bookForm, preferred_date: e.target.value})}
                    data-testid="book-date"
                  />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={bookForm.preferred_time}
                    onChange={(e) => setBookForm({...bookForm, preferred_time: e.target.value})}
                    data-testid="book-time"
                  />
                </div>
              </div>
            )}

            <div>
              <Label>Reason</Label>
              <Input
                placeholder="Consultation reason"
                value={bookForm.reason}
                onChange={(e) => setBookForm({...bookForm, reason: e.target.value})}
                data-testid="book-reason"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Additional notes..."
                value={bookForm.notes}
                onChange={(e) => setBookForm({...bookForm, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookDialog(false)}>Cancel</Button>
            <Button onClick={handleBookForPatient} disabled={!selectedPatient || submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-book-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Video className="w-4 h-4 mr-1" />}
              {bookForm.is_instant ? 'Start Consultation' : 'Book Consultation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
